/**
 * Files API — CRUD operations for the Supabase files table.
 *
 * Each record represents a client file submitted by the adviser
 * and processed through Inbox's workflow stages.
 */

import supabase from './supabaseClient';

/**
 * Map a Supabase row (snake_case) to the internal file record format (camelCase).
 */
function mapRowToFile(row) {
  return {
    id: row.id,
    fileId: row.file_id,
    clientName: row.client_name || '',
    appointmentDate: row.appointment_date || null,
    submittedBy: row.submitted_by || '',
    submittedAt: row.submitted_at || null,
    status: row.status || 'submitted',
    drawnBy: row.drawn_by || null,
    drawnAt: row.drawn_at || null,
    pausedAt: row.paused_at || null,
    pausedTotalMs: row.paused_total_ms || 0,
    resumedAt: row.resumed_at || null,
    sentToSignAt: row.sent_to_sign_at || null,
    signedAt: row.signed_at || null,
    completedAt: row.completed_at || null,
    slaBreached: row.sla_breached || false,
    activeDaysUsed: row.active_days_used || 0,
    qualifiesForBonus: row.qualifies_for_bonus || false,
    voiceNoteUrl: row.voice_note_url || null,
    // DEPRECATED (29 September 2026): `transcription` and `ai_summary` are
    // read-only historical data from the removed Whisper + AI summary
    // pipeline. Nothing writes or displays them any more; the columns are
    // kept so existing rows keep their data. The adviser's written Client
    // File Brief lives in written_instructions instead.
    transcription: row.transcription || null,
    aiSummary: row.ai_summary || null,
    attachments: row.attachments || [],
    // Set once the file's attachment storage objects have been purged
    // (CLAUDE.md §29). Metadata above is retained; this only marks that the
    // working copies are gone. NULL = still in storage (or never had any).
    attachmentsPurgedAt: row.attachments_purged_at || null,
    atworkScreenshotUrl: row.atwork_screenshot_url || null,
    notes: row.notes || null,
    writtenInstructions: row.written_instructions || null,
    // Handover memo — the Administration Assistant's "Notes for New
    // Business" typed at handover (migration 27). Locked once the memo has
    // been generated; NOT a file note and never a stat input.
    handoverNotes: row.handover_notes || null,
    handoverNotesLockedAt: row.handover_notes_locked_at || null,
    rejectionReason: row.rejection_reason || null,
    outlookEventId: row.outlook_event_id || null,
    monthYear: row.month_year || '',
    workCategory: row.work_category || '',
    priority: row.priority || 'Normal',
    clientLanguage: row.client_language || 'English',
    // Multi-case screenshot gate. Both NULL on legacy files; when populated,
    // the assistant must upload one AtWork screenshot per case before the
    // file can move from `signed` to `completed` (DB trigger enforced).
    caseCount: row.case_count == null ? null : row.case_count,
    caseLabels: Array.isArray(row.case_labels) ? row.case_labels : null,
    // Back-link to the preparation request this file was pre-filled from
    // (migration 14; written by the "Create file folder" action on a
    // completed prep request, §26.9). NAVIGATIONAL REFERENCE ONLY — no
    // stats, SLA, bonus or deadline logic ever traverses this link.
    sourcePrepSheetId: row.source_prep_sheet_id || null,
    // Test-record marker (migration 23). Set only by the Adviser via the
    // "Load as test file" checkbox at submission; defaults to false. A
    // follow-up change will exclude test records from stats and pipeline
    // rules — nothing reads this yet.
    isTest: row.is_test === true,
    // Adviser commission splits. Always present once the migration has
    // been applied (default seeded server-side); we still defensively
    // fall back to the canonical 70/30 split if the column is missing
    // or malformed.
    commissionSplits: Array.isArray(row.commission_splits)
      ? row.commission_splits
      : [
          { name: 'M Lourens', percent: 70 },
          { name: 'Optimum PFP', percent: 30 },
        ],
  };
}

/**
 * Convert camelCase update fields to snake_case for Supabase.
 */
function mapUpdatesToRow(updates) {
  const mapping = {
    fileId: 'file_id',
    clientName: 'client_name',
    appointmentDate: 'appointment_date',
    submittedBy: 'submitted_by',
    submittedAt: 'submitted_at',
    status: 'status',
    drawnBy: 'drawn_by',
    drawnAt: 'drawn_at',
    pausedAt: 'paused_at',
    pausedTotalMs: 'paused_total_ms',
    resumedAt: 'resumed_at',
    sentToSignAt: 'sent_to_sign_at',
    signedAt: 'signed_at',
    completedAt: 'completed_at',
    slaBreached: 'sla_breached',
    activeDaysUsed: 'active_days_used',
    qualifiesForBonus: 'qualifies_for_bonus',
    voiceNoteUrl: 'voice_note_url',
    // transcription / ai_summary deliberately NOT mapped: deprecated,
    // never written (29 September 2026).
    attachments: 'attachments',
    attachmentsPurgedAt: 'attachments_purged_at',
    atworkScreenshotUrl: 'atwork_screenshot_url',
    notes: 'notes',
    writtenInstructions: 'written_instructions',
    handoverNotes: 'handover_notes',
    handoverNotesLockedAt: 'handover_notes_locked_at',
    rejectionReason: 'rejection_reason',
    outlookEventId: 'outlook_event_id',
    monthYear: 'month_year',
    workCategory: 'work_category',
    priority: 'priority',
    clientLanguage: 'client_language',
    caseCount: 'case_count',
    caseLabels: 'case_labels',
    commissionSplits: 'commission_splits',
  };

  const row = {};
  for (const [key, value] of Object.entries(updates)) {
    const col = mapping[key];
    if (col) {
      row[col] = value;
    }
  }
  return row;
}

/**
 * Fetch all files, ordered by submitted_at descending.
 */
export async function getAllFiles() {
  const { data, error } = await supabase
    .from('files')
    .select('*')
    .order('submitted_at', { ascending: false })
    .limit(500);

  if (error) throw new Error(`Failed to fetch files: ${error.message}`);
  return (data || []).map(mapRowToFile);
}

/**
 * Fetch a single file by its Supabase row ID.
 */
export async function getFileById(itemId) {
  const { data, error } = await supabase
    .from('files')
    .select('*')
    .eq('id', itemId)
    .single();

  if (error) throw new Error(`Failed to fetch file: ${error.message}`);
  return mapRowToFile(data);
}

/**
 * Create a new file record.
 *
 * @param {object} fileData — file record fields
 * @returns {Promise<object>} created file record
 */
export async function createFile(fileData) {
  const row = {
    file_id: fileData.fileId || crypto.randomUUID(),
    client_name: fileData.clientName,
    appointment_date: fileData.appointmentDate || null,
    submitted_by: fileData.submittedBy,
    submitted_at: new Date().toISOString(),
    status: 'submitted',
    paused_total_ms: 0,
    sla_breached: false,
    qualifies_for_bonus: false,
    work_category: fileData.workCategory || '',
    priority: fileData.priority || 'Normal',
    notes: fileData.notes || '',
    attachments: fileData.attachments || [],
    month_year: formatMonthYear(new Date()),
    outlook_event_id: fileData.outlookEventId || null,
    voice_note_url: fileData.voiceNoteUrl || null,
    // The Client File Brief ("Summary" on the form). The legacy
    // transcription / ai_summary columns are never written (deprecated).
    written_instructions: fileData.writtenInstructions || null,
    client_language: fileData.clientLanguage || 'English',
    // Multi-case fields are only set when the adviser declared at least one
    // case on the form. Legacy single-screenshot files leave these NULL.
    case_count:
      fileData.caseCount != null && fileData.caseCount > 0
        ? fileData.caseCount
        : null,
    case_labels:
      Array.isArray(fileData.caseLabels) && fileData.caseLabels.length > 0
        ? fileData.caseLabels
        : null,
    // Test-record marker — strictly boolean, defaults to false so normal
    // submissions are always real records (migration 23).
    is_test: fileData.isTest === true,
    // Optional back-link to the prep request this file was pre-filled from
    // (§26.9). Null on ordinary submissions.
    source_prep_sheet_id: fileData.sourcePrepSheetId || null,
    // Commission splits — fall through to the DB default (70 / 30) if the
    // form did not pass anything in.
    ...(Array.isArray(fileData.commissionSplits)
      ? { commission_splits: fileData.commissionSplits }
      : {}),
  };

  const { data, error } = await supabase
    .from('files')
    .insert(row)
    .select()
    .single();

  if (error) throw new Error(`Failed to create file: ${error.message}`);
  return mapRowToFile(data);
}

/**
 * Update specific fields on a file record.
 *
 * @param {string} itemId — Supabase row ID (UUID)
 * @param {object} updates — camelCase fields to update
 * @returns {Promise<object>} updated file record
 */
export async function updateFile(itemId, updates) {
  const row = mapUpdatesToRow(updates);

  const { data, error } = await supabase
    .from('files')
    .update(row)
    .eq('id', itemId)
    .select()
    .single();

  if (error) throw new Error(`Failed to update file: ${error.message}`);
  return mapRowToFile(data);
}

/**
 * Delete a file record (admin use only).
 */
export async function deleteFile(itemId) {
  const { error } = await supabase
    .from('files')
    .delete()
    .eq('id', itemId);

  if (error) throw new Error(`Failed to delete file: ${error.message}`);
}

/**
 * Get files filtered by status.
 */
export async function getFilesByStatus(status) {
  const { data, error } = await supabase
    .from('files')
    .select('*')
    .eq('status', status)
    .order('submitted_at', { ascending: false });

  if (error) throw new Error(`Failed to fetch files by status: ${error.message}`);
  return (data || []).map(mapRowToFile);
}

/**
 * Get files for a specific month (format: "2026-04").
 */
export async function getFilesByMonth(monthYear) {
  const { data, error } = await supabase
    .from('files')
    .select('*')
    .eq('month_year', monthYear)
    .order('submitted_at', { ascending: false });

  if (error) throw new Error(`Failed to fetch files by month: ${error.message}`);
  return (data || []).map(mapRowToFile);
}

/**
 * Format a date as "YYYY-MM" for the month_year field.
 */
function formatMonthYear(date) {
  const d = new Date(date);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
}
